import Backbone from 'backbone';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {FindPrimersOptions} from 'molbio/find-primers/FindPrimersConstants';
import FindPrimersTabMenu from 'molbio/find-primers/FindPrimersTabMenu';
import {FindPrimersTabState} from 'molbio/find-primers/FindPrimersTabState';
import notifications from 'notifications';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import * as net from 'util/Net';

export class FindPrimersOptionsView extends Backbone.View<FindPrimersTabState> {
  saving!: boolean;

  static initClass(): void {
    this.prototype.tagName = 'div';
  }

  override initialize(): void {
    this.saving = false;
    this.listenTo(this.model, 'selectChange', () => this.render());
    this.listenTo(this.model.get('sequence'), 'change:currentVersion', () => this.render());
    this.listenTo(this.model.get('sequence').get('_sequence'), 'change:locked', () => this.render());
    this.listenTo(this.model, 'change:findType', () => this.render());
    this.listenTo(this.model, 'change:highlightMatches', () => this.render());
  }

  async addSelectedPrimers(): Promise<void> {
    if (this.saving) {
      return;
    }
    const sequence = this.model.get('sequence');
    analytics.track('primer', 'attach_selected_click', {
      product: ANALYTICS_MODULE.MOLBIO,
      sequence_id: sequence.get('id'),
    });
    const sequencePrimers = sequence.get('_sequence').sequencePrimers();
    const newPrimers = this.model.getNewPrimers();
    if (newPrimers.length === 0) {
      notifications.error('Select new primers to add first.');
      return;
    }

    // Use bulk form (array of primers) which is faster and avoids potential deadlocks.
    try {
      this.saving = true;
      this.render();
      const {primers} = await net.postJSONPromise({
        url: sequencePrimers.url(),
        data: newPrimers.map((newPrimer) => newPrimer.toJSON()),
      });
      for (const primer of newPrimers) {
        primer.set({disabled: true});
      }
      // Get the old 'ghost' primers out of the sequencePrimers model...
      sequencePrimers.remove(newPrimers);
      // ...and add the new, persisted 'real' ones.
      sequencePrimers.add(primers);
      await sequence.updateVersions();
      notifications.success(`Successfully added ${newPrimers.length} primer(s).`);
      analytics.track('primer', 'added_primers', {
        product: ANALYTICS_MODULE.MOLBIO,
        source: 'find-primers',
        sequence_api_id: sequence.id,
        primer_count: newPrimers.length,
      });
    } catch (e) {
      notifications.error('An error occurred while trying to save a primer.');
      throw e;
    } finally {
      this.saving = false;
      this.render();
    }
  }

  override render(): this {
    renderToDOM(
      <FindPrimersTabMenu
        highlight={this.model.get('highlightMatches')}
        setHighlight={(highlightMatches: boolean) => {
          this.model.set('highlightMatches', highlightMatches);
        }}
        showSave={this.model.get('findType') !== FindPrimersOptions.BASES}
        disableSave={
          this.saving ||
          !this.model.get('sequence').allowFeatureEdits() ||
          this.model
            .get('sequence')
            .sequencePrimers()
            .filter((model) => model.isNew()).length === 0
        }
        addSelectedPrimers={async () => {
          await this.addSelectedPrimers();
        }}
      />,
      this.$el[0]
    );
    return this;
  }
}

FindPrimersOptionsView.initClass();
